import { useQuery } from '@apollo/client';
import type { CareerRoleType } from '@coursera-web/cds-labs';
import * as Sentry from '@sentry/react';

import LohpPathwayCareerRolesQuery from 'bundles/front-page/redesign-modules/career-academy/queries/LohpPathwayCareerRolesQuery.graphql';
import type {
  LohpPathwayCareerRolesQuery as LohpPathwayCareerRolesQueryType,
  LohpPathwayCareerRolesQueryVariables,
} from 'bundles/front-page/redesign-modules/career-academy/queries/__generated__/LohpPathwayCareerRolesQuery';

export type LohpCareerRole = Pick<
  CareerRoleType,
  'name' | 'shortDescription' | 'slug' | 'jobPostingData' | 'roleCardImage' | 'id'
> & {
  imagePrimaryColor?: string;
};

/**
 * The LohpPathwayCareerRolesData query returns imagePrimaryColor as hex color code that are
 * strong colors not suited for backgrounds. This maps to muted/background-appropriate CDS tokens.
 */
const IMAGE_PRIMARY_COLOR_BACKGROUND_MAPPINGS: Record<string, string> = {
  '#faaf00': 'var(--cds-color-yellow-25)',
  '#002761': 'var(--cds-color-blue-50)',
  '#0056d2': 'var(--cds-color-blue-25)',
  '#8040ed': 'var(--cds-color-purple-25)',
};

export const normalizeLohpCareerRolesData = (
  queryResponse?: LohpPathwayCareerRolesQueryType | null
): LohpCareerRole[] | undefined => {
  const elements = queryResponse?.PathwaySkillCareerRoles?.queryRolesByPathwayLevelAndCategory?.entities?.elements;

  if (!elements?.length) {
    return undefined;
  }

  return elements
    .map((element) => {
      if (element?.__typename !== 'Skill_CareerRole') {
        return null;
      }

      return {
        id: element.id || '',
        name: element.name || '',
        description: element.shortDescription || element.description || '',
        slug: element.slug || '',
        roleCardImage: element.roleCardImage,
        imagePrimaryColor: IMAGE_PRIMARY_COLOR_BACKGROUND_MAPPINGS[element.imagePrimaryColor?.toLowerCase() ?? ''],
        jobPostingData: element.jobPostingData
          ? {
              medianSalary: element.jobPostingData.medianSalary,
              numJobsAvailable: element.jobPostingData.numJobsAvailable,
              currencyCode: element.jobPostingData.currencyCode,
            }
          : undefined,
      };
    })
    .filter(Boolean) as LohpCareerRole[];
};

export function useLohpPathwayCareerRolesData(limit: number = 5) {
  const { data, loading, error } = useQuery<LohpPathwayCareerRolesQueryType, LohpPathwayCareerRolesQueryVariables>(
    LohpPathwayCareerRolesQuery,
    {
      variables: { limit },
      ssr: false,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all' as const,
      context: { clientName: 'gatewayGql' },
      onError: (err) =>
        Sentry.captureException(err, {
          extra: {
            message: 'Error fetching LOHP Pathway career roles data',
          },
        }),
    }
  );

  return {
    data: normalizeLohpCareerRolesData(data),
    loading,
    error,
  };
}

export default useLohpPathwayCareerRolesData;
